<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <treeselect
        v-model="selectedValues"
        :multiple="true"
        :options="values"
        :load-options="loadMore"
        @input="changed"
        value-consists-of="LEAF_PRIORITY"
      >
        <template #before-list>
          <b-link class="ml-3" @click.prevent.stop="selectAll">Select/Deselect All</b-link>
        </template>
        <template #value-label="{node}">
          {{ isGrouped ? node.group + ': ' + node.label : node.label }}
        </template>
        <template #option-label="{node}">
          <label class="vue-treeselect__label">
            <span
              v-if="node.id === 'search'"
              class="clickable"
              @click="
                searchModal = true;
                searchFilter = node;
              "
              >search...</span
            >
            <span v-else>{{ node.label }}</span>
          </label>
        </template>
      </treeselect>
    </b-overlay>

    <b-modal v-model="searchModal" title="Search" hide-modal>
      <b-row>
        <b-col cols="12">
          <b-form ref="search-form" @submit.stop.prevent="search">
            <b-input
              v-show="!searching"
              class="filter-search-input"
              type="search"
              v-model="searchTerm"
              placeholder="Search keyword"
              required
              @focus="
                $event.target.setSelectionRange(0, $event.target.value.length)
              "
            ></b-input>
            <small class="text-muted filter-info"
              >Search to reduce filter's options</small
            >
            <b-icon
              v-show="searching"
              icon="three-dots"
              animation="cylon"
              font-scale="4"
            ></b-icon>
          </b-form>
        </b-col>
      </b-row>
      <template v-slot:modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="primary" @click="search">Submit</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash';
import http from '@/mixins/http';
import { LOAD_CHILDREN_OPTIONS } from '@riophae/vue-treeselect';

export default {
  name: 'FilterGeneral',
  props: {
    filter: {
      type: Object,
      required: true,
    },
    selectedCubeId: {
      type: Number,
      required: true,
    },
  },
  mixins: [http],
  mounted() {
    this.getValues();
    if (this.filter.options.length) {
      this.selectedValues = _.map(this.filter.options, option => {
        let value = option.value;
        //handle legacy code/data
        value = value.replace('[Month Year].[Month Year]', '[Month Year]');
        value = value.replace('[Year Month].[Year Month]', '[Year Month]');
        return value;
      });
    }
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        if (this.filter.options.length) {
          this.selectedValues = _.map(this.filter.options, option => {
            let value = option.value;
            //handle legacy code/data
            value = value.replace('[Month Year].[Month Year]', '[Month Year]');
            value = value.replace('[Year Month].[Year Month]', '[Year Month]');
            return value;
          });
        }
      },
    },
  },
  data() {
    return {
      isLoading: false,
      selectedValues: [],
      values: [],

      searchModal: false,
      searchTerm: '',
      searchFilter: {},
      searching: false,
    };
  },
  computed: {
    isGrouped() {
      return _.some(this.filter.options, option => {
        return !!(
          option.group &&
          option.group.length > 0 &&
          option.group !== 'All' &&
          option.group !== '-'
        );
      });
    },
  },
  methods: {
    selectAll() {
      let allOptions = _.map(this.values, v => {
        return v.id;
      });
      if (_.isEqual(_.sortBy(this.selectedValues), _.sortBy(allOptions))) {
        this.selectedValues = [];
      } else {
        this.selectedValues = allOptions;
      }
    },
    getValues() {
      const filter = this.filter.level;

      this.isLoading = true;
      this.axios
        .get('/api/dataGrabber/' + this.selectedCubeId + '/filterOptions', {
          params: filter,
        })
        .then(resp => {
          if (resp.data) {
            let options = resp.data.options;
            let values = _.chain(options)
              .map(option => {
                let a = {
                  id: option.value,
                  label: option.text,
                  group: option.group,
                };
                if (this.filter.isHierarchy) {
                  a.children = null;
                }
                return a;
              })
              .orderBy(['label'])
              .value();
            if (values.length >= 100) {
              values.push({
                id: 'search',
                label: '',
                isDisabled: true,
              });
            }
            this.values = values;
          }
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    loadMore({ action, parentNode, callback }) {
      if (action === LOAD_CHILDREN_OPTIONS) {
        let levelNumber = parentNode.levelNumber ? parentNode.levelNumber : 1;
        let next = _.find(this.filter.hierarchies, {
          levelNumber: '' + (levelNumber + 1),
        });
        next.filterId = parentNode.id;
        let nextNext = _.find(this.filter.hierarchies, {
          levelNumber: '' + (levelNumber + 2),
        });

        this.axios
          .get('/api/datagrabber/' + this.selectedCubeId + '/filteroptions', {
            params: next,
          })
          .then(resp => {
            if (resp.data) {
              let options = _.cloneDeep(resp.data.options);
              options = _.map(options, v => {
                if (!v.text || !v.text.trim() || 0 === v.text.length) {
                  v.text = '(null)';
                }
                let option = {
                  id: v.value,
                  label: v.text,
                  levelNumber: levelNumber + 1,
                };
                if (nextNext) {
                  option.children = null;
                }
                return option;
              });
              if (options.length >= 100) {
                options.push({
                  id: 'search',
                  label: '',
                  isDisabled: true,
                });
              }
              parentNode.children = options;
              callback();
            }
          })
          .catch(function(ex) {
            // eslint-disable-next-line no-console
            console.log(ex);
            callback(
              new Error('Failed to load options: please try again later.')
            );
          });
      }
    },
    search() {
      const form = this.$refs['search-form'];

      if (!form.reportValidity()) {
        return;
      }

      const levelNumber = this.searchFilter.levelNumber
        ? this.searchFilter.levelNumber
        : 1;
      let next = _.find(this.filter.hierarchies, {
        levelNumber: '' + (levelNumber + 1),
      });

      let level = _.cloneDeep(this.filter.level);
      level.searchKeyword = this.searchTerm;
      this.searching = true;
      this.axios
        .get('/api/dataGrabber/' + this.selectedCubeId + '/filterOptions', {
          params: level,
        })
        .then(resp => {
          if (resp.data) {
            let existingOptions = _.filter(this.values, a => {
              return this.checkSelectedOption(a);
            });

            let options = _.cloneDeep(resp.data.options);
            options = _.map(options, v => {
              if (!v.text || !v.text.trim() || 0 === v.text.length) {
                v.text = '(null)';
              }
              let option = {
                id: v.value,
                label: v.text,
                levelNumber: levelNumber + 1,
              };
              if (next) {
                option.children = null;
              }
              return option;
            });
            options.push({
              id: 'search',
              label: '',
              isDisabled: true,
            });
            options = [...existingOptions, ...options];
            if (this.searchFilter.parentNode) {
              this.searchFilter.parentNode.options = options;
            } else {
              this.values = options;
            }
            this.searchTerm = '';
          }
        })
        .catch(function(ex) {
          // eslint-disable-next-line no-console
          console.log(ex);
        })
        .then(() => {
          this.searchModal = false;
          this.searching = false;
        });
    },
    checkSelectedOption(obj) {
      let exist = false;
      if (obj.options) {
        _.each(obj.options, function(a) {
          exist = exist || this.checkSelectedOption(a);
        });
      }
      exist = _.some(this.selectedValues, selectedOption => {
        return selectedOption === obj.value;
      });
      return exist;
    },
    changed(selectedValues) {
      this.$emit(
        'input',
        _.map(selectedValues, option => {
          return { group: '-', text: '-', value: option };
        })
      );
    },
  },
};
</script>

<style scoped>
.btn-select-all {
  font-size: 0.9em;
  cursor: pointer;
}
</style>